import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { createSelect2Manager } from "../lib/select2_manager"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"

// "Add states to profile" modal on the State Licenses card. Holds a select2
// multi-select of US states (the shared _licensed_states_select partial, scoped
// to this controller), pre-selected with the agent's current licensed states.
// Saving PATCHes the selection to the profile and renders the Turbo Stream the
// server returns (which replaces #state-licenses-card so the new states appear in
// the card's "Add a state" dropdown), then closes the modal.
//
// select2 is initialized on `shown.bs.modal` so it measures a visible element
// (initializing inside a hidden modal yields zero width), with dropdownParent set
// to the modal so the option list renders above the backdrop. Target/method names
// match the shared partial's scope wiring: `licensedStates` + `updateStatesCounter`.
//
// BUTTON-9: Save to Profile is disabled (with its reason) while the selection is
// the profile's saved one, and again if an edit is reverted. The baseline is the
// native <select>'s selection at connect (select2 only renders it) and is retaken
// after each successful save; an unsaved edit left by Cancel stays dirty on the
// next open. The <select multiple> keeps option order, so its fingerprint compares
// the selected set regardless of click order.
export default class extends Controller {
  static targets = ["licensedStates", "counter", "saveButton", "message"]
  static values = { url: String }

  connect() {
    this.select2Manager = createSelect2Manager()
    this.boundOnShown = this.onShown.bind(this)
    this.element.addEventListener("shown.bs.modal", this.boundOnShown)
    this._takeBaseline()
    this.updateStatesCounter()
  }

  disconnect() {
    this.element.removeEventListener("shown.bs.modal", this.boundOnShown)
    if (this.select2Manager) this.select2Manager.destroyAll()
    this.select2Manager = null
  }

  onShown() {
    // Clear any stale error from a previous failed attempt when the modal re-opens.
    if (this.hasMessageTarget) this.messageTarget.classList.add("d-none")
    this._syncSaveButton()
    if (this.initialized || !this.hasLicensedStatesTarget) return
    this.initialized = true
    this.select2Manager.init(
      this.licensedStatesTarget,
      () => ({
        placeholder: "Select states",
        allowClear: true,
        multiple: true,
        width: "100%",
        dropdownParent: this.element,
      }),
      {
        onInitialized: (_element, $element) => {
          const handler = () => this.updateStatesCounter()
          $element.on("change", handler)
          return () => $element.off("change", handler)
        },
      },
    )
    this.updateStatesCounter()
  }

  selectAll(event) {
    event.preventDefault()
    this._setAll(true)
  }

  removeAll(event) {
    event.preventDefault()
    this._setAll(false)
  }

  _setAll(selected) {
    Array.from(this.licensedStatesTarget.options).forEach((option) => { option.selected = selected })
    // Notify select2 (jQuery) so its tags re-render, then refresh the counter.
    const jQuery = window.jQuery || window.$
    if (jQuery) jQuery(this.licensedStatesTarget).trigger("change")
    this.updateStatesCounter()
  }

  // Runs on every selection change (select2's change, All / None), so it also
  // re-gates Save.
  updateStatesCounter() {
    this._syncSaveButton()
    if (!this.hasCounterTarget || !this.hasLicensedStatesTarget) return
    this.counterTarget.textContent = `(${this.licensedStatesTarget.selectedOptions.length} selected)`
  }

  _selectionToken() {
    return this.hasLicensedStatesTarget ? fieldToken(this.licensedStatesTarget) : ""
  }

  _takeBaseline() {
    this.baseline = this._selectionToken()
  }

  _syncSaveButton() {
    if (!this.hasSaveButtonTarget || this.saving) return
    const dirty = this._selectionToken() !== this.baseline
    syncGateReason(this.saveButtonTarget, { ready: dirty, reason: SAVE_UNCHANGED_REASON })
  }

  async save() {
    if (!this.hasLicensedStatesTarget) return
    const codes = Array.from(this.licensedStatesTarget.selectedOptions).map((option) => option.value)
    // The native `required` attribute isn't enforced for a request.js submit, so
    // guard here — an empty submit would otherwise wipe the agent's states.
    if (codes.length === 0) {
      // Neutral phrasing: the modal both adds and trims states, so "to add" would
      // confuse a user clearing selections. The Profile hint covers full removal.
      this._showError("Select at least one state.")
      return
    }

    const body = new FormData()
    codes.forEach((code) => body.append("licensed_states[]", code))

    this.saving = true
    this.saveButtonTarget.disabled = true
    try {
      // @rails/request.js handles CSRF + auto-renders the success Turbo Stream
      // (which replaces the State Licenses card).
      const response = await patch(this.urlValue, { body, responseKind: "turbo-stream" })
      if (response.ok) {
        // Reflect the just-saved selection on the native <select> so the
        // re-initialized select2 (see _closeModal) opens showing the saved truth,
        // not a stale page-load selection — otherwise a later save could drop states.
        this._applySelection(codes)
        this._takeBaseline()
        this._closeModal()
      } else {
        // Failure returns the validation message as plain text (no Turbo Stream),
        // so show it inline and keep the modal open for retry — no duplicate toast.
        const message = ((await response.text) || "").trim()
        this._showError(message || "Could not save states. Please try again.")
      }
    } catch {
      this._showError("Could not save states. Please try again.")
    } finally {
      this.saving = false
      this._syncSaveButton()
    }
  }

  _applySelection(codes) {
    if (!this.hasLicensedStatesTarget) return
    const selected = new Set(codes)
    Array.from(this.licensedStatesTarget.options).forEach((option) => {
      option.selected = selected.has(option.value)
    })
  }

  _closeModal() {
    // Tear down select2 and reset the init guard so the next open re-initializes
    // against the current (just-saved) native select — a stale selection
    // persisting across opens could drop states on the following save.
    if (this.select2Manager) this.select2Manager.destroyAll()
    this.initialized = false

    const Modal = window.bootstrap?.Modal
    if (!Modal) return
    const instance = Modal.getInstance(this.element) || Modal.getOrCreateInstance(this.element)
    if (instance) instance.hide()
  }

  _showError(message) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = message
    this.messageTarget.classList.remove("d-none")
  }
}
